// src/components/ModalOrdenCobroRemitos.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Table } from "react-bootstrap";
import moment from "moment";

import {
  getRemitosPendientesCliente,
  getRemitoById,
} from "../helpers/rutaRemitos";
import { postOrdenCobroRemito } from "../helpers/rutaOrdenCobroRemitos";
import { getSaldosTesoreria } from "../helpers/rutaTesoreria";
import { generarPDFPendientesCliente } from "../helpers/pdfPendientesCliente";

const fmtFechaDoc = (d) => (d ? moment.utc(d).format("DD/MM/YYYY") : "");

const $ = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const IVA_105 = 1.105;
const LOGO_SAN_PANCRACIO = "/SanPancracio.png";

const renderCuentaBancoLabel = (c) =>
  `${c.banco || c.descripcion || c.alias} — Saldo: $ ${$(c.saldoActual || 0)}`;

const toISOorNull = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  const d = new Date(yyyy_mm_dd + "T00:00:00");
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const cleanOrEmpty = (s) => String(s || "").trim();
const cleanOrNull = (s) => {
  const t = String(s || "").trim();
  return t ? t : null;
};

// Calcula Venta A / Venta B (blanco/negro) del lado VENTA
function calcularTotalesVenta(rem) {
  let totalA = Number(rem.totalVentaA || 0); // blanco + IVA
  let totalB = Number(rem.totalVentaB || 0); // negro

  const kg = Number(rem.kgRecibido || 0);
  const pv = Number(rem.precioVenta || 0);
  const base = rem.totalVenta != null ? Number(rem.totalVenta || 0) : kg * pv;

  // Si no vienen A y B, recalculo con porcentajeVenta
  if (totalA <= 0.01 && totalB <= 0.01 && base > 0) {
    const porcB = Number(rem.porcentajeVenta || 0) / 100; // % negro
    const ventaNegro = base * porcB;
    const ventaBlancoBase = base - ventaNegro;
    const ventaBlancoIVA = ventaBlancoBase * IVA_105;

    totalA = ventaBlancoIVA;
    totalB = ventaNegro;
  }

  return { totalA, totalB };
}

export default function ModalOrdenCobroRemitos({
  show,
  onHide,
  cliente,
  proveedor = null, // filtro opcional
  preseleccion = [],
}) {
  const hoy = useMemo(() => moment().format("YYYY-MM-DD"), []);
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [obs, setObs] = useState("");

  const [pendientes, setPendientes] = useState([]);
  const [seleccion, setSeleccion] = useState([]);

  // Filtro opcional por proveedor
  const [filtrarProveedor, setFiltrarProveedor] = useState(false);

  // Qué conceptos de VENTA se cobran
  const [cobraA, setCobraA] = useState(false); // Venta A (blanco)
  const [cobraB, setCobraB] = useState(false); // Venta B (negro)

  // cobros / retenciones
  const [medios, setMedios] = useState([]);
  const [rets, setRets] = useState([]);

  // combos tesorería
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);

  // estilos parejos (labels iguales)
  const labelStyle = useMemo(
    () => ({ fontSize: 12, fontWeight: 700, marginBottom: 2 }),
    []
  );
  const helpStyle = useMemo(() => ({ fontSize: 11, color: "#6c757d" }), []);
  const cardStyle = useMemo(
    () => ({
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 12,
      background: "#f8fafc",
    }),
    []
  );

  const gridStyle = useMemo(
    () => ({
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
      gap: 10,
      alignItems: "end",
    }),
    []
  );

  const updMedio = useCallback((idx, patch) => {
    setMedios((p) => p.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  }, []);

  const toggleOpen = useCallback((idx) => {
    setMedios((p) =>
      p.map((m, i) => (i === idx ? { ...m, _open: !(m._open !== false) } : m))
    );
  }, []);

  const makeCheque = useCallback(
    () => ({
      medio: "cheque",
      importe: 0,
      _open: true,

      // comunes
      fechaCheque: hoy,
      fechaLiquidacion: "",
      beneficiario: "",
      cuitBeneficiario: "",
      observaciones: "",
      referencia: "",

      // cheque
      nroCheque: "",
      bancoEmisor: "",
      librador: "",
      cuitLibrador: "",
      fechaPagoDiferido: "", // opcional legacy
    }),
    [hoy]
  );

  const makeICheq = useCallback(
    () => ({
      medio: "icheq",
      importe: 0,
      _open: true,

      // comunes
      fechaCheque: hoy, // fecha iCheq
      fechaLiquidacion: "",
      beneficiario: "",
      cuitBeneficiario: "",
      observaciones: "",
      referencia: "",

      // icheq
      idICheq: "",
      emisor: "",
      cuitEmisor: "",
    }),
    [hoy]
  );

  // ------------- totales pendientes cliente -------------
  const totalesPendientes = pendientes.reduce(
    (acc, r) => {
      const pendA = Number(
        r.pendienteA ??
          Math.max(
            0,
            Number(r.totalVentaA || 0) - Number(r.cobradoVentaA || 0)
          )
      );
      const pendB = Number(
        r.pendienteB ??
          Math.max(
            0,
            Number(r.totalVentaB || 0) - Number(r.cobradoVentaB || 0)
          )
      );

      const saldo = Number((pendA + pendB).toFixed(2));

      acc.ventaA += pendA;
      acc.ventaB += pendB;
      acc.saldo += saldo;
      return acc;
    },
    { ventaA: 0, ventaB: 0, saldo: 0 }
  );

  const handlePdfPendientes = () => {
    generarPDFPendientesCliente({
      cliente,
      proveedor: filtrarProveedor ? proveedor : null,
      pendientes,
      totales: totalesPendientes,
      fecha,
      logoUrl: LOGO_SAN_PANCRACIO,
      empresa: {
        nombre: "SAN PANCRACIO SRL",
        direccion:
          "Avda. Peron 1600 - Oficina 29 Piso 1 - Yerba Buena - Tucumán",
        telefono: "Tel: 0381-3008829",
        email: "contacto@sanpancracio.com",
      },
    }).catch((err) => {
      console.error("Error generando PDF de pendientes (cliente):", err);
      alert("No se pudo generar el PDF de pendientes.");
    });
  };

  // Σ selección
  const totalSeleccion = useMemo(
    () =>
      seleccion.reduce(
        (a, r) => a + Number(r.saldo ?? r.saldoPendiente ?? 0),
        0
      ),
    [seleccion]
  );

  const totalCobros = useMemo(
    () => medios.reduce((a, m) => a + Number(m.importe || 0), 0),
    [medios]
  );

  const totalRets = useMemo(
    () => rets.reduce((a, r) => a + Number(r.importe || 0), 0),
    [rets]
  );

  const montoAplicable = useMemo(
    () => Math.max(0, Number(totalCobros) - Number(totalRets)),
    [totalCobros, totalRets]
  );

  // Distribución automática aplicable en Venta A / B
  const aplicacionesDistribuidas = useMemo(() => {
    let restante = montoAplicable;
    const orden = [...seleccion].sort(
      (a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0)
    );

    const apps = [];

    for (const r of orden) {
      if (restante <= 0.01) break;

      const pendAFull = Number(
        r.pendienteA ??
          Math.max(
            0,
            Number(r.totalVentaA || 0) - Number(r.cobradoVentaA || 0)
          )
      );
      const pendBFull = Number(
        r.pendienteB ??
          Math.max(
            0,
            Number(r.totalVentaB || 0) - Number(r.cobradoVentaB || 0)
          )
      );

      const saldoTotal = Number((pendAFull + pendBFull).toFixed(2));
      if (saldoTotal <= 0.01) continue;

      let pendienteA = cobraA ? pendAFull : 0;
      let pendienteB = cobraB ? pendBFull : 0;

      const totalPendConceptos = pendienteA + pendienteB;
      if (totalPendConceptos <= 0.01) continue;

      let restanteRemito = Math.min(saldoTotal, restante);
      let aplicadoA = 0;
      let aplicadoB = 0;

      const conceptos = [
        { key: "A", max: pendienteA },
        { key: "B", max: pendienteB },
      ];

      for (const c of conceptos) {
        if (restanteRemito <= 0.01) break;
        if (c.max <= 0.01) continue;

        const usar = Math.min(c.max, restanteRemito);
        if (c.key === "A") aplicadoA += usar;
        else aplicadoB += usar;

        restanteRemito -= usar;
      }

      const aplicadoTotal = aplicadoA + aplicadoB;
      if (aplicadoTotal <= 0.01) continue;

      apps.push({
        remito: r._id,
        aplicado: aplicadoTotal,
        aplicadoA,
        aplicadoB,
      });

      restante -= aplicadoTotal;
    }

    return { apps, restante };
  }, [seleccion, montoAplicable, cobraA, cobraB]);

  const diferenciaVisual = useMemo(
    () => (totalSeleccion - montoAplicable).toFixed(2),
    [totalSeleccion, montoAplicable]
  );

  // Warnings mínimos
  const cobroWarnings = useMemo(() => {
    const w = [];
    medios.forEach((m, i) => {
      const n = i + 1;

      if (m.medio === "efectivo" && !m.caja)
        w.push(`Cobro #${n}: seleccione una Caja.`);
      if (m.medio === "transferencia" && !m.cuentaBancaria)
        w.push(`Cobro #${n}: seleccione una Cuenta bancaria.`);

      if (m.medio === "cheque") {
        if (!cleanOrEmpty(m.nroCheque)) w.push(`Cobro #${n}: falta Nro Cheque.`);
        if (!m.fechaCheque) w.push(`Cobro #${n}: falta Fecha Cheque.`);
      }
      if (m.medio === "icheq") {
        if (!cleanOrEmpty(m.idICheq)) w.push(`Cobro #${n}: falta ID iCheq.`);
        if (!m.fechaCheque) w.push(`Cobro #${n}: falta Fecha iCheq.`);
      }
    });
    return w;
  }, [medios]);

  const canSubmit =
    seleccion.length > 0 && totalCobros > 0 && montoAplicable > 0;

  // ----------- cargar data (pendientes + tesorería) -----------
  useEffect(() => {
    if (!show || !(cliente?._id || cliente)) return;

    (async () => {
      const baseList = await getRemitosPendientesCliente(
        cliente?._id || cliente,
        filtrarProveedor ? (proveedor?._id || proveedor) : undefined
      );

      const list = await Promise.all(
        (baseList || []).map(async (r) => {
          try {
            const resp = await getRemitoById(r._id);
            const rem = resp?.remito || resp || {};
            const { totalA, totalB } = calcularTotalesVenta(rem);

            const cobradoCliente = Number(rem.cobradoCliente || 0);
            const cobradoVentaA = Number(rem.cobradoVentaA || 0);
            const cobradoVentaB = Number(rem.cobradoVentaB || 0);

            const pendienteA = Math.max(0, totalA - cobradoVentaA);
            const pendienteB = Math.max(0, totalB - cobradoVentaB);
            const saldoPendiente = Number((pendienteA + pendienteB).toFixed(2));

            return {
              ...r,
              totalVentaA: totalA,
              totalVentaB: totalB,
              cobradoCliente,
              cobradoVentaA,
              cobradoVentaB,
              pendienteA,
              pendienteB,
              saldoPendiente,
            };
          } catch (err) {
            console.error("Error cargando detalle remito", r._id, err);
            return {
              ...r,
              totalVentaA: 0,
              totalVentaB: 0,
              cobradoCliente: 0,
              cobradoVentaA: 0,
              cobradoVentaB: 0,
              pendienteA: 0,
              pendienteB: 0,
              saldoPendiente: 0,
            };
          }
        })
      );

      const ordenadas = (list || [])
        .slice()
        .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));

      const map = new Map(ordenadas.map((r) => [r._id, r]));
      const seed = [
        ...preseleccion,
        ...seleccion.filter((x) => map.has(x._id)),
      ];

      const sel = [];
      seed.forEach((s) => {
        const r = map.get(s._id);
        if (!r) return;

        const pendA = Number(
          r.pendienteA ??
            Math.max(
              0,
              Number(r.totalVentaA || 0) - Number(r.cobradoVentaA || 0)
            )
        );
        const pendB = Number(
          r.pendienteB ??
            Math.max(
              0,
              Number(r.totalVentaB || 0) - Number(r.cobradoVentaB || 0)
            )
        );

        const saldo = Number((pendA + pendB).toFixed(2));
        if (saldo > 0.01) sel.push({ ...r, saldo });
      });

      setPendientes(ordenadas);
      setSeleccion(sel);

      try {
        const s = await getSaldosTesoreria();
        if (s?.ok) {
          setCajas(Array.isArray(s.cajas) ? s.cajas : []);
          setCuentas(Array.isArray(s.cuentas) ? s.cuentas : []);
        }
      } catch (err) {
        console.error("Error cargando saldos tesorería", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cliente?._id, proveedor?._id, filtrarProveedor]);

  // ----------- selección -----------
  const toggle = (r) => {
    setSeleccion((prev) => {
      const ex = prev.find((x) => x._id === r._id);
      if (ex) return prev.filter((x) => x._id !== r._id);

      const pendA = Number(
        r.pendienteA ??
          Math.max(
            0,
            Number(r.totalVentaA || 0) - Number(r.cobradoVentaA || 0)
          )
      );
      const pendB = Number(
        r.pendienteB ??
          Math.max(
            0,
            Number(r.totalVentaB || 0) - Number(r.cobradoVentaB || 0)
          )
      );
      const saldo = Number((pendA + pendB).toFixed(2));
      return saldo > 0.01 ? [...prev, { ...r, saldo }] : prev;
    });
  };

  const tildarTodas = () =>
    setSeleccion(
      pendientes
        .map((r) => {
          const pendA = Number(
            r.pendienteA ??
              Math.max(
                0,
                Number(r.totalVentaA || 0) - Number(r.cobradoVentaA || 0)
              )
          );
          const pendB = Number(
            r.pendienteB ??
              Math.max(
                0,
                Number(r.totalVentaB || 0) - Number(r.cobradoVentaB || 0)
              )
          );
          const saldo = Number((pendA + pendB).toFixed(2));
          return { ...r, saldo };
        })
        .filter((r) => r.saldo > 0.01)
    );

  const destildarTodas = () => setSeleccion([]);

  // ----------- cobros / retenciones -----------
  const addMedio = (m) => setMedios((p) => [...p, m]);
  const rmMedio = (i) => setMedios((p) => p.filter((_, idx) => idx !== i));
  const addRet = (r) => setRets((p) => [...p, r]);
  const rmRet = (i) => setRets((p) => p.filter((_, idx) => idx !== i));

  // ----------- guardar -----------
  const guardar = async () => {
    try {
      if (!canSubmit) return;

      if (cobroWarnings.length > 0) {
        const seguir = window.confirm(
          "Hay advertencias:\n- " +
            cobroWarnings.join("\n- ") +
            "\n\n¿Deseás continuar de todas formas?"
        );
        if (!seguir) return;
      }

      const payload = {
        cliente: cliente?._id || cliente,
        proveedor: filtrarProveedor ? proveedor?._id || proveedor : undefined,
        fecha,
        observaciones: obs,
        aplicaciones: aplicacionesDistribuidas.apps,

        cobros: medios.map((m) => {
          const base = {
            medio: m.medio,
            importe: Number(m.importe || 0),
            referencia: cleanOrEmpty(m.referencia),

            // comunes cheque/icheq
            fechaCheque: toISOorNull(m.fechaCheque) || undefined,
            fechaLiquidacion: toISOorNull(m.fechaLiquidacion) || undefined,
            beneficiario: cleanOrNull(m.beneficiario),
            cuitBeneficiario: cleanOrNull(m.cuitBeneficiario),
            observaciones: cleanOrNull(m.observaciones),
          };

          if (m.medio === "efectivo") return { ...base, caja: m.caja };
          if (m.medio === "transferencia")
            return { ...base, cuentaBancaria: m.cuentaBancaria };

          if (m.medio === "cheque") {
            return {
              ...base,
              nroCheque: cleanOrNull(m.nroCheque),
              bancoEmisor: cleanOrNull(m.bancoEmisor),
              librador: cleanOrNull(m.librador),
              cuitLibrador: cleanOrNull(m.cuitLibrador),
              fechaPagoDiferido: toISOorNull(m.fechaPagoDiferido) || undefined,
            };
          }

          if (m.medio === "icheq") {
            return {
              ...base,
              idICheq: cleanOrNull(m.idICheq),
              emisor: cleanOrNull(m.emisor),
              cuitEmisor: cleanOrNull(m.cuitEmisor),
            };
          }

          return base;
        }),

        retenciones: rets.map((r) => ({
          tipo: (r.tipo || "IIBB").toUpperCase(),
          base: Number(r.base || 0),
          porcentaje: Number(r.porcentaje || 0),
          importe: Number(r.importe || 0),
          comprobante: r.comprobante || "",
        })),
      };

      const res = await postOrdenCobroRemito(payload);
      if (res?.ok) {
        alert("Orden de cobro guardada correctamente.");
        onHide(true);
      } else {
        alert(res?.msg || "Error al guardar.");
      }
    } catch (e) {
      console.error("OC Remitos error:", e?.response?.data || e);
      alert(e?.response?.data?.msg || e.message || "Error al guardar.");
    }
  };

  const nombreCli =
    (cliente?.razonsocial || cliente?.nombre || "").toString();
  const nombreProv =
    (proveedor?.razonsocial || proveedor?.nombre || "")?.toString() || "";

  return (
    <Modal show={show} onHide={() => onHide(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Nueva Orden de Cobro (Remitos)</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* headers */}
        <Row className="mb-2">
          <Col md={4}>
            <Form.Label>Cliente</Form.Label>
            <Form.Control readOnly value={nombreCli} />
          </Col>
          <Col md={4}>
            <Form.Label>Proveedor (filtro opcional)</Form.Label>
            <Form.Control readOnly value={nombreProv} placeholder="(sin filtro)" />
            <div className="mt-1">
              <Form.Check
                type="switch"
                id="filtrar-por-proveedor"
                label="Filtrar por este proveedor"
                checked={filtrarProveedor}
                onChange={(e) => setFiltrarProveedor(e.target.checked)}
              />
            </div>
          </Col>
          <Col md={4}>
            <Form.Label>Fecha</Form.Label>
            <Form.Control
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </Col>
        </Row>

        {/* pendientes */}
        <Row className="align-items-center mb-2">
          <Col>
            <b>Remitos pendientes del cliente</b>
          </Col>
          <Col className="text-end">
            <Button size="sm" variant="outline-secondary" onClick={tildarTodas}>
              Tildar todos
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={destildarTodas}
            >
              Destildar todos
            </Button>{" "}
            <Button size="sm" variant="outline-primary" onClick={handlePdfPendientes}>
              PDF pendientes
            </Button>{" "}
            {Boolean(seleccion.length) && (
              <span className="ms-2">
                Seleccionados: <b>{seleccion.length}</b> — Σ:{" "}
                <b>${$(totalSeleccion)}</b>
              </span>
            )}
          </Col>
        </Row>

        <div
          style={{
            maxHeight: 260,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          <Table size="sm" bordered hover className="m-0">
            <thead>
              <tr
                style={{
                  position: "sticky",
                  top: 0,
                  background: "#f8f9fa",
                  zIndex: 2,
                }}
              >
                <th style={{ width: 40 }}></th>
                <th>Remito</th>
                <th>Fecha</th>
                <th style={{ textAlign: "right" }}>
                  Pendiente Venta A (blanco + IVA)
                </th>
                <th style={{ textAlign: "right" }}>
                  Pendiente Venta B (negro)
                </th>
                <th style={{ textAlign: "right" }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((r) => {
                const checked = !!seleccion.find((x) => x._id === r._id);

                const pendA = Number(
                  r.pendienteA ??
                    Math.max(
                      0,
                      Number(r.totalVentaA || 0) -
                        Number(r.cobradoVentaA || 0)
                    )
                );
                const pendB = Number(
                  r.pendienteB ??
                    Math.max(
                      0,
                      Number(r.totalVentaB || 0) -
                        Number(r.cobradoVentaB || 0)
                    )
                );

                const saldo = Number((pendA + pendB).toFixed(2));
                const disabled = saldo <= 0.01;
                const id = `sel-r-v-${r._id}`;

                return (
                  <tr
                    key={r._id}
                    style={disabled ? { opacity: 0.5 } : undefined}
                  >
                    <td style={{ width: 40, textAlign: "center" }}>
                      <input
                        id={id}
                        type="checkbox"
                        className="form-check-input m-0 p-0"
                        disabled={disabled}
                        checked={checked}
                        onChange={() => toggle(r)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <label htmlFor={id} className="w-100 m-0">
                        #{String(r.codrem).padStart(6, "0")}
                      </label>
                    </td>
                    <td>
                      <label htmlFor={id} className="w-100 m-0">
                        {fmtFechaDoc(r.fecha)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={id} className="w-100 m-0">
                        ${$(pendA)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={id} className="w-100 m-0">
                        ${$(pendB)}
                      </label>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <label htmlFor={id} className="w-100 m-0">
                        ${$(saldo)}
                      </label>
                    </td>
                  </tr>
                );
              })}
              {!pendientes.length && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    Sin pendientes para este cliente/proveedor
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* resumen pendientes */}
        <div
          className="mt-2 p-2 rounded"
          style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #90caf9",
            color: "#0d47a1",
            fontWeight: "bold",
          }}
        >
          <Row>
            <Col md={4}>
              Σ Pendiente Venta A (blanco + IVA): ${$(totalesPendientes.ventaA)}
            </Col>
            <Col md={4}>
              Σ Pendiente Venta B (negro): ${$(totalesPendientes.ventaB)}
            </Col>
            <Col md={4}>
              Σ Saldo pendiente total: ${$(totalesPendientes.saldo)}
            </Col>
          </Row>
        </div>

        {/* conceptos */}
        <Row className="mt-2">
          <Col>
            <Form.Check
              inline
              type="checkbox"
              id="cobra-a"
              label="Cobrar Venta A (blanco)"
              checked={cobraA}
              onChange={(e) => setCobraA(e.target.checked)}
            />
            <Form.Check
              inline
              type="checkbox"
              id="cobra-b"
              label="Cobrar Venta B (negro)"
              checked={cobraB}
              onChange={(e) => setCobraB(e.target.checked)}
            />
          </Col>
        </Row>

        {/* cobros / retenciones */}
        <Row className="mt-3">
          <Col md={6}>
            <h6>Cobros</h6>

            <Button
              size="sm"
              onClick={() =>
                addMedio({ medio: "transferencia", importe: 0, cuentaBancaria: "" })
              }
            >
              + Transferencia
            </Button>{" "}
            <Button size="sm" onClick={() => addMedio(makeCheque())}>
              + Cheque
            </Button>{" "}
            <Button size="sm" onClick={() => addMedio(makeICheq())}>
              + iCheq
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addMedio({ medio: "efectivo", importe: 0, caja: "" })}
            >
              + Efectivo
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Medio</th>
                  <th>Destino / Datos</th>
                  <th style={{ width: 180 }}>Importe</th>
                  <th style={{ width: 55 }}></th>
                </tr>
              </thead>

              <tbody>
                {medios.map((m, i) => {
                  const isCheque = m.medio === "cheque";
                  const isICheq = m.medio === "icheq";
                  const open = m._open !== false;

                  const selCaja = cajas.find((c) => String(c._id) === String(m.caja));
                  const selCta = cuentas.find((c) => String(c._id) === String(m.cuentaBancaria));

                  const resumenCheque = [
                    cleanOrEmpty(m.nroCheque) ? `Nro: ${m.nroCheque}` : "",
                    cleanOrEmpty(m.bancoEmisor) ? `Banco: ${m.bancoEmisor}` : "",
                    m.fechaCheque ? `F: ${moment(m.fechaCheque).format("DD/MM/YYYY")}` : "",
                  ].filter(Boolean).join(" — ");

                  const resumenICheq = [
                    cleanOrEmpty(m.idICheq) ? `ID: ${m.idICheq}` : "",
                    cleanOrEmpty(m.emisor) ? `Emisor: ${m.emisor}` : "",
                    m.fechaCheque ? `F: ${moment(m.fechaCheque).format("DD/MM/YYYY")}` : "",
                  ].filter(Boolean).join(" — ");

                  return (
                    <>
                      {/* FILA PRINCIPAL */}
                      <tr key={`main-${i}`}>
                        <td style={{ verticalAlign: "top" }}>
                          <Form.Select
                            size="sm"
                            value={m.medio}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "transferencia") {
                                updMedio(i, {
                                  medio: v,
                                  importe: 0,
                                  cuentaBancaria: "",
                                  caja: "",
                                  referencia: "",
                                  _open: false,
                                });
                                return;
                              }
                              if (v === "efectivo") {
                                updMedio(i, {
                                  medio: v,
                                  importe: 0,
                                  caja: "",
                                  cuentaBancaria: "",
                                  referencia: "",
                                  _open: false,
                                });
                                return;
                              }
                              if (v === "cheque") {
                                updMedio(i, makeCheque());
                                return;
                              }
                              if (v === "icheq") {
                                updMedio(i, makeICheq());
                                return;
                              }
                            }}
                          >
                            <option value="transferencia">Transferencia</option>
                            <option value="cheque">Cheque</option>
                            <option value="icheq">iCheq</option>
                            <option value="efectivo">Efectivo</option>
                          </Form.Select>

                          {(isCheque || isICheq) && (
                            <button
                              type="button"
                              className="btn btn-link p-0 mt-1"
                              style={{ fontSize: 12, textDecoration: "none" }}
                              onClick={() => toggleOpen(i)}
                            >
                              {open ? "▲ Ocultar datos" : "▼ Mostrar datos"}
                            </button>
                          )}
                        </td>

                        {/* Destino / Datos (compacto) */}
                        <td>
                          {m.medio === "efectivo" && (
                            <div className="d-flex gap-2 align-items-center">
                              <Form.Select
                                size="sm"
                                value={m.caja || ""}
                                style={{ minWidth: 280 }}
                                onChange={(e) => updMedio(i, { caja: e.target.value })}
                              >
                                <option value="">-- Caja --</option>
                                {cajas.map((c) => (
                                  <option key={c._id} value={c._id}>
                                    {c.descripcion} — Saldo: ${$(c.saldoActual || 0)}
                                  </option>
                                ))}
                              </Form.Select>
                              {selCaja && (
                                <span className="text-muted">
                                  Saldo: ${$(selCaja.saldoActual || 0)}
                                </span>
                              )}
                            </div>
                          )}

                          {m.medio === "transferencia" && (
                            <div className="d-flex gap-2 align-items-center">
                              <Form.Select
                                size="sm"
                                value={m.cuentaBancaria || ""}
                                style={{ minWidth: 280 }}
                                onChange={(e) =>
                                  updMedio(i, { cuentaBancaria: e.target.value })
                                }
                              >
                                <option value="">-- Cuenta --</option>
                                {cuentas.map((c) => (
                                  <option key={c._id} value={c._id}>
                                    {renderCuentaBancoLabel(c)}
                                  </option>
                                ))}
                              </Form.Select>
                              {selCta && (
                                <span className="text-muted">
                                  Saldo: ${$(selCta.saldoActual || 0)}
                                </span>
                              )}
                            </div>
                          )}

                          {(isCheque || isICheq) && (
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              {isCheque ? (resumenCheque || "—") : (resumenICheq || "—")}
                            </div>
                          )}
                        </td>

                        {/* Importe */}
                        <td style={{ verticalAlign: "top" }}>
                          <Form.Label style={labelStyle}>Importe *</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="0.01"
                            value={m.importe}
                            onChange={(e) =>
                              updMedio(i, { importe: Number(e.target.value) })
                            }
                          />
                        </td>

                        <td style={{ verticalAlign: "top" }}>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => rmMedio(i)}
                          >
                            x
                          </Button>
                        </td>
                      </tr>

                      {/* FILA DETALLE (colSpan) */}
                      {(isCheque || isICheq) && open && (
                        <tr key={`detail-${i}`}>
                          <td colSpan={4}>
                            <div style={cardStyle}>
                              <div style={gridStyle}>
                                {/* Identificador */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Nro Cheque *" : "ID iCheq *"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.nroCheque || "") : (m.idICheq || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { nroCheque: e.target.value }
                                        : { idICheq: e.target.value }
                                      )
                                    }
                                    placeholder={isCheque ? "Ej: 123456" : "Ej: 1121"}
                                  />
                                </div>

                                {/* Banco / Emisor */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Banco emisor" : "Emisor"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.bancoEmisor || "") : (m.emisor || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { bancoEmisor: e.target.value }
                                        : { emisor: e.target.value }
                                      )
                                    }
                                    placeholder="Ej: Santander"
                                  />
                                </div>

                                {/* Fecha Cheque / iCheq */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Fecha Cheque" : "Fecha iCheq"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={m.fechaCheque || ""}
                                    onChange={(e) =>
                                      updMedio(i, { fechaCheque: e.target.value })
                                    }
                                  />
                                </div>

                                {/* Fecha liquidación */}
                                <div>
                                  <Form.Label style={labelStyle}>Fecha liquidación</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={m.fechaLiquidacion || ""}
                                    onChange={(e) =>
                                      updMedio(i, { fechaLiquidacion: e.target.value })
                                    }
                                  />
                                  <div style={helpStyle}>Opcional (cuando se acredita)</div>
                                </div>

                                {/* CUIT emisor/librador */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "CUIT Librador" : "CUIT Emisor"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.cuitLibrador || "") : (m.cuitEmisor || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { cuitLibrador: e.target.value }
                                        : { cuitEmisor: e.target.value }
                                      )
                                    }
                                    placeholder="Sólo números"
                                  />
                                </div>

                                {/* Librador / Cobrado a */}
                                <div>
                                  <Form.Label style={labelStyle}>
                                    {isCheque ? "Cobrado a (Librador)" : "Cobrado a (Emisor)"}
                                  </Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={isCheque ? (m.librador || "") : (m.emisor || "")}
                                    onChange={(e) =>
                                      updMedio(i, isCheque
                                        ? { librador: e.target.value }
                                        : { emisor: e.target.value }
                                      )
                                    }
                                    placeholder="Nombre / razón social"
                                  />
                                </div>

                                {/* Fecha pago diferido (solo cheque) */}
                                <div>
                                  {isCheque ? (
                                    <>
                                      <Form.Label style={labelStyle}>Fecha pago diferido</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        type="date"
                                        value={m.fechaPagoDiferido || ""}
                                        onChange={(e) =>
                                          updMedio(i, { fechaPagoDiferido: e.target.value })
                                        }
                                      />
                                      <div style={helpStyle}>Legacy / opcional</div>
                                    </>
                                  ) : (
                                    <div />
                                  )}
                                </div>

                                {/* Beneficiario */}
                                <div>
                                  <Form.Label style={labelStyle}>Pagado a (Beneficiario)</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.beneficiario || ""}
                                    onChange={(e) =>
                                      updMedio(i, { beneficiario: e.target.value })
                                    }
                                    placeholder="Ej: SAN PANCRACIO SRL"
                                  />
                                </div>

                                {/* CUIT Beneficiario */}
                                <div>
                                  <Form.Label style={labelStyle}>CUIT Beneficiario</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.cuitBeneficiario || ""}
                                    onChange={(e) =>
                                      updMedio(i, { cuitBeneficiario: e.target.value })
                                    }
                                    placeholder="Sólo números"
                                  />
                                </div>

                                {/* Referencia */}
                                <div>
                                  <Form.Label style={labelStyle}>Referencia (opcional)</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.referencia || ""}
                                    onChange={(e) =>
                                      updMedio(i, { referencia: e.target.value })
                                    }
                                    placeholder="Texto libre"
                                  />
                                </div>

                                {/* Observaciones */}
                                <div style={{ gridColumn: "1 / -1" }}>
                                  <Form.Label style={labelStyle}>Observaciones</Form.Label>
                                  <Form.Control
                                    size="sm"
                                    value={m.observaciones || ""}
                                    onChange={(e) =>
                                      updMedio(i, { observaciones: e.target.value })
                                    }
                                    placeholder="Notas internas"
                                  />
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}

                {!medios.length && (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      Agregá al menos un cobro
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {cobroWarnings.length > 0 && (
              <ul className="mt-2" style={{ color: "crimson", fontSize: 13 }}>
                {cobroWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </Col>

          {/* retenciones */}
          <Col md={6}>
            <h6>Retenciones</h6>
            <Button
              size="sm"
              onClick={() => addRet({ tipo: "IIBB", base: 0, porcentaje: 0, importe: 0 })}
            >
              + IIBB
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addRet({ tipo: "GAN", base: 0, porcentaje: 0, importe: 0 })}
            >
              + Ganancias
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addRet({ tipo: "IVA", base: 0, porcentaje: 0, importe: 0 })}
            >
              + IVA
            </Button>{" "}
            <Button
              size="sm"
              onClick={() => addRet({ tipo: "OTRA", base: 0, porcentaje: 0, importe: 0 })}
            >
              + Otra
            </Button>

            <Table size="sm" className="mt-2">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Base</th>
                  <th>%</th>
                  <th>Importe</th>
                  <th>Comp.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rets.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Select
                        size="sm"
                        value={r.tipo}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i ? { ...x, tipo: e.target.value } : x
                            )
                          )
                        }
                      >
                        <option value="IIBB">IIBB</option>
                        <option value="GAN">Ganancias</option>
                        <option value="IVA">IVA</option>
                        <option value="OTRA">OTRA</option>
                      </Form.Select>
                    </td>
                    <td style={{ width: 110 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.base || 0}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i
                                ? { ...x, base: Number(e.target.value) }
                                : x
                            )
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.porcentaje || 0}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i
                                ? {
                                    ...x,
                                    porcentaje: Number(e.target.value),
                                  }
                                : x
                            )
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 120 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={r.importe || 0}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i
                                ? { ...x, importe: Number(e.target.value) }
                                : x
                            )
                          )
                        }
                      />
                    </td>
                    <td style={{ width: 140 }}>
                      <Form.Control
                        size="sm"
                        placeholder="Comprobante"
                        value={r.comprobante || ""}
                        onChange={(e) =>
                          setRets((p) =>
                            p.map((x, idx) =>
                              idx === i
                                ? { ...x, comprobante: e.target.value }
                                : x
                            )
                          )
                        }
                      />
                    </td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => rmRet(i)}
                      >
                        x
                      </Button>
                    </td>
                  </tr>
                ))}
                {!rets.length && (
                  <tr>
                    <td colSpan={6} className="text-muted">
                      Agregá retenciones si corresponde
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
            />
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={3}>
            <b>Σ Selección:</b> ${$(totalSeleccion)}
          </Col>
          <Col md={3}>
            <b>Σ Cobros:</b> ${$(totalCobros)}
          </Col>
          <Col md={3}>
            <b>Σ Retenciones:</b> ${$(totalRets)}
          </Col>
          <Col md={3}>
            <b>Σ Aplicable:</b> ${$(montoAplicable)}
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <div className="text-muted">
              Diferencia (Selección − Aplicable):{" "}
              <b
                style={{
                  color:
                    Math.abs(Number(diferenciaVisual)) <= 0.01
                      ? "green"
                      : "crimson",
                }}
              >
                {$(diferenciaVisual)}
              </b>{" "}
              (informativa)
            </div>

            {aplicacionesDistribuidas.restante > 0.01 && (
              <div className="text-danger mt-1">
                * Hay importe sin poder aplicarse por falta de saldo seleccionado.
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => onHide(false)}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={guardar} disabled={!canSubmit}>
          Guardar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
